import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getLocalUser } from "../../../components/functions/utilities";
import { useAppDispatch, useAppSelector } from "../../../redux/reduxHooks";
import { UserRole } from "../../../interfaces/interfaces";
import { CHANNEL_SECTION, CONNECTION_HUB_MODE } from "../api/connectionHub.types";
import { REQUEST_STATUS } from "../constants/connectionHub.constants";
import { resetConnectionHubContext } from "../state/connectionHubSlice";
import { fetchConnectionChannels } from "../state/connectionHub.thunks";
import { selectConnectionCatalog, selectConnectionHubSession } from "../state/connectionHub.selectors";
import type { ConnectionHubCloseEvent, ConnectionHubSuccessResult } from "../api/connectionHub.types";
import { resolveQuickConnectRoute } from "../adapters/quickConnect.adapter";
import { useConnectionHub } from "./useConnectionHub";

/** The connections page, where ConnectionsGrid lists every channel. */
export const CONNECTIONS_PAGE_PATH = "/workspaceStore";

/** A legacy integration partner, which is what the quick-connect dispatcher still keys off. */
export interface QuickConnectTarget {
  _id?: string;
  companyName?: string;
}

export interface SmartLaunchOptions {
  source?: string;
  connectionId?: string;
  startNew?: boolean;
  onSuccess?: (result: ConnectionHubSuccessResult) => void;
  onClose?: (event: ConnectionHubCloseEvent) => void;
}

export interface QuickLaunchOptions {
  /** Zoho has its own modal, owned by the host. Without one the user is sent to the connections page. */
  onZoho?: () => void;
}

export interface ConnectLauncher {
  connectSmart: (ipIds: string[], options?: SmartLaunchOptions) => void;
  connectQuick: (target: QuickConnectTarget | null | undefined, options?: QuickLaunchOptions) => void;
  /** Routes by the channel's own section, for screens whose cards mix quick and smart. */
  connectChannel: (
    target: QuickConnectTarget | null | undefined,
    options?: SmartLaunchOptions & QuickLaunchOptions
  ) => void;
  openConnectionsPage: () => void;
}

/**
 * The one entry point a host screen needs. Smart channels open the hub's own modal and quick channels
 * go straight to OAuth — neither passes through the legacy "Connect Accounts" list.
 */
export const useConnectLauncher = (): ConnectLauncher => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { open, openQuick } = useConnectionHub();
  const catalog = useAppSelector(selectConnectionCatalog);
  const session = useAppSelector(selectConnectionHubSession);
  const currentUser = getLocalUser();
  const isAgency = currentUser?.role === UserRole.Agency;
  const currentClient = useAppSelector((state) => state.agencyConfig.currentClient);
  const agencyClientId = isAgency ? currentClient?.id ?? null : null;

  // connectChannel needs the catalog to tell quick from smart, and a host screen may never have
  // rendered the grid that normally loads it. The thunk is status-guarded, so this is a no-op once warm.
  useEffect(() => {
    if (session.isOpen || session.agencyClientId === agencyClientId) return;
    dispatch(resetConnectionHubContext(agencyClientId));
  }, [agencyClientId, dispatch, session.agencyClientId, session.isOpen]);

  useEffect(() => {
    if (session.agencyClientId !== agencyClientId || catalog.status !== REQUEST_STATUS.IDLE) return;
    void dispatch(fetchConnectionChannels({ agencyClientId }));
  }, [agencyClientId, catalog.status, dispatch, session.agencyClientId]);

  const openConnectionsPage = useCallback((): void => {
    navigate(CONNECTIONS_PAGE_PATH);
  }, [navigate]);

  // The mode comes from the count rather than the caller: `open` silently drops a launch whose mode
  // and ipId count disagree, which looks to the user like a button that does nothing.
  const connectSmart = useCallback(
    (ipIds: string[], options: SmartLaunchOptions = {}): void => {
      const unique = Array.from(new Set(ipIds.filter(Boolean)));
      if (!unique.length) {
        openConnectionsPage();
        return;
      }
      open({
        ipIds: unique,
        mode: unique.length === 1 ? CONNECTION_HUB_MODE.SINGLE : CONNECTION_HUB_MODE.MULTI,
        agencyClientId,
        ...options,
      });
    },
    [agencyClientId, open, openConnectionsPage]
  );

  const connectQuick = useCallback(
    (target: QuickConnectTarget | null | undefined, options: QuickLaunchOptions = {}): void => {
      const route = resolveQuickConnectRoute({ ip: target, isAgency });

      // An unavailable channel has nothing to open, so the click does nothing (the hub never toasts).
      if (route.route === "unavailable") return;
      if (route.route === "zoho") {
        if (options.onZoho) options.onZoho();
        else openConnectionsPage();
        return;
      }

      // The account list first, not the redirect: the user sees what is already connected and, for an
      // agency, which client they are about to connect for. The redirect happens from inside it.
      openQuick({ ...target, agencyClientId });
    },
    [agencyClientId, isAgency, openConnectionsPage, openQuick]
  );

  const connectChannel = useCallback(
    (target: QuickConnectTarget | null | undefined, options: SmartLaunchOptions & QuickLaunchOptions = {}): void => {
      const channels = catalog.data?.sections.flatMap((section) => section.channels) ?? [];
      const match = channels.find(
        (channel) => channel.ipId === target?._id || channel.ipName === target?.companyName
      );
      // Unknown means the catalog has not landed yet, or the channel is not connectable here. Either
      // way the connections page is the honest destination — never the legacy modal.
      if (!match) {
        openConnectionsPage();
        return;
      }
      if (match.section === CHANNEL_SECTION.SMART) connectSmart([match.ipId], options);
      else connectQuick(target, options);
    },
    [catalog.data, connectQuick, connectSmart, openConnectionsPage]
  );

  return useMemo(
    () => ({ connectSmart, connectQuick, connectChannel, openConnectionsPage }),
    [connectChannel, connectQuick, connectSmart, openConnectionsPage]
  );
};

export default useConnectLauncher;
